import { z } from 'zod'
import { tolerantArray } from '@/schemas/shared/tolerantArray'
import { ZipCodeSchema } from '../customer/address'
import { ShippingQuotesResponseSchema } from '../shared/shipping'
import { OptionalTextSchema } from '../shared/text'
import { ProductCardSchema, ProductSchema, ProductSearchResultSchema, ProductSizeOptionSchema } from './product'

/**
 * Requisições e respostas dos endpoints do catálogo (`Glofi.API/Controllers/ProductController.cs` e as recomendações
 * do `CartController`). Formatos conferidos com `tests/Glofi.API.Tests/Snapshots/Products` no GlofiEcomm.
 */

const text = OptionalTextSchema

// ===== LISTAGEM: GET product/list-products?term&page&pageSize =====

/** Os filtros vão serializados dentro de `term` (ver `buildProductListTerm`); `page` começa em 0. */
export const ProductListRequestSchema = z.object({
    term: z.string(),
    page: z.number().int().min(0),
    pageSize: z.number().int().min(1).max(100),
})

export const ProductListResponseSchema = z.object({
    products: tolerantArray(ProductCardSchema, 'produtos da listagem'),
})

// ===== VITRINE: GET product/get-by-list-group/{id} =====

/** Produtos da lista na ordem dela; lista inexistente responde `{ products: [] }`. */
export const ShowcaseResponseSchema = z.object({
    products: tolerantArray(ProductCardSchema, 'produtos da vitrine'),
})

// ===== BUSCA DO NAVBAR: POST product/search =====

/** Com `tagsSearch`, a API busca pela tag e ignora `keyword`. No máximo 20 resultados. */
export const ProductSearchRequestSchema = z.object({
    keyword: z.string().optional(),
    tagsSearch: z.string().optional(),
})

export const ProductSearchResponseSchema = tolerantArray(ProductSearchResultSchema, 'resultados da busca')

// ===== PDP: GET product/get-by-slug/{slug} =====

const ProductFoundSchema = z.object({
    success: z.literal(true),
    product: ProductSchema,
    /** Tamanhos do mesmo grupo (inclui o próprio produto); vazio quando o produto não tem grupo. */
    productSizes: z
        .array(ProductSizeOptionSchema)
        .nullish()
        .transform((value) => value ?? []),
})

/** Produto inexistente ou slug vazio: a API responde 200 com `success: false` (não 404). */
const ProductNotFoundSchema = z.object({
    success: z.literal(false),
    message: text,
    slug: z.string().nullish(),
})

export const ProductBySlugResponseSchema = z.discriminatedUnion('success', [ProductFoundSchema, ProductNotFoundSchema])

// ===== FRETE DA PDP: POST /product/calc-product-shipping =====

/** CEP sem máscara (a API também limpa `-`, `/` e `.`). Erros: 400 com `"CEP inválido"` ou `{ message }`. */
export const ProductShippingRequestSchema = z.object({
    zipcode: z.string(),
    productId: z.string(),
    quantity: z.number().int().min(1),
})

/** Resposta do frete da PDP: a cotação de `schemas/shared/shipping.ts`, a mesma do carrinho. */
export const ProductShippingResponseSchema = ShippingQuotesResponseSchema

// ===== RECOMENDAÇÕES: GET cart/product-user-recomendation/{customerId} e POST cart/product-recomendation =====

/**
 * Produto que a vitrine mostra: a API marca o que não está à venda com `active: false`, `visible: false` ou
 * `deleted: true` (a PDP desses responde 404). Campo ausente não exclui.
 */
const isOnSale = (product: unknown): boolean => {
    if (product === null || typeof product !== 'object') return false
    const { active, visible, deleted } = product as { active?: unknown; visible?: unknown; deleted?: unknown }
    return active !== false && visible !== false && deleted !== true
}

/**
 * Sem recomendação, a do cliente responde 200 com `success: false` e a por produtos responde 400; os dois casos viram
 * lista vazia. A API devolve o produto inteiro (`GetByIds`), com `null` para id sem produto, e sem filtrar o que saiu
 * da vitrine (o motor de IA recomenda produto inativo; débito do backend): os nulos e os que não estão à venda saem da
 * lista antes do parse, que descarta `active` e `visible`.
 */
export const ProductRecommendationResponseSchema = z.object({
    success: z.boolean().optional(),
    message: text,
    products: z.preprocess(
        (value) => (Array.isArray(value) ? value.filter(isOnSale) : value),
        tolerantArray(ProductCardSchema, 'produtos recomendados'),
    ),
})

// ===== FORMULÁRIO: CEP do frete na PDP =====

/** CEP pela regra única do endereço (`ZipCodeSchema` do customer); sai só com os dígitos. */
export const ProductShippingFormSchema = z.object({
    zipcode: ZipCodeSchema('Informe um CEP válido.'),
})

// ===== TIPOS =====

export type IProductListRequest = z.infer<typeof ProductListRequestSchema>
export type IProductListResponse = z.infer<typeof ProductListResponseSchema>
export type IShowcaseResponse = z.infer<typeof ShowcaseResponseSchema>
export type IProductSearchRequest = z.infer<typeof ProductSearchRequestSchema>
export type IProductBySlugResponse = z.infer<typeof ProductBySlugResponseSchema>
export type IProductShippingRequest = z.infer<typeof ProductShippingRequestSchema>
export type IProductShippingResponse = z.infer<typeof ProductShippingResponseSchema>
export type IProductRecommendationResponse = z.infer<typeof ProductRecommendationResponseSchema>
export type IProductShippingForm = z.input<typeof ProductShippingFormSchema>
